/**
 * D-2 订单 —— 契约 §7 D-2 / trade 的 `OrderDtos.java`。
 *
 * ★ 契约硬约束：**只读 + 可取消，永不建单**。下单只走 Agent 的确认卡片。
 *   所以本模块只有 list / detail / cancel 三个动作，没有、也不许有 create。
 */
import { z } from 'zod'

/**
 * 我方公共状态机（trade.proto `OrderState`，去掉 `UNSPECIFIED`）。
 *
 * ⚠ 这是**我方**状态，不是供应商状态码。列表筛选 `?status=` 传的就是这些名字，
 *   认不出的后端会 400（刻意不静默当「不过滤」）。
 */
export const ORDER_STATES = [
  'ORDER_CREATED',
  'ORDER_PENDING_PAYMENT',
  'ORDER_PAID',
  'ORDER_FULFILLING',
  'ORDER_COMPLETED',
  'ORDER_CANCELLED',
  'ORDER_REFUNDING',
  'ORDER_REFUNDED',
  'ORDER_FAILED',
] as const
export type OrderState = (typeof ORDER_STATES)[number]

/**
 * 列表一行（`OrderSummaryView`）。刻意窄：不含 `domainPayload`、行程段、内部主键。
 *
 * ⚠ `state` 用 `z.string()` 而不是 enum：后端将来加一个状态，前端不该整页解析失败 ——
 *   展示层按已知名给标签、认不出就原样显示。
 * ⚠ `totalAmount` 是**元**（后端 BigDecimal，JSON 里是数字如 `1280.00`）。JS 会读成 number，
 *   够精确用于展示；**别拿它做任何金额加减**（那才有精度风险），要算金额去后端。
 */
export const orderSummarySchema = z.looseObject({
  orderNo: z.string().min(1),
  /** flight / hotel / …。 */
  domain: z.string().nullable().default(null),
  state: z.string().min(1),
  totalAmount: z.number().nullable().default(null),
  currency: z.string().nullable().default(null),
  /** ISO-8601 本地时间串（如 `2026-09-02T14:30:00`）。 */
  createdAt: z.string().nullable().default(null),
  /** ⚠ **支付并推单成功后才有值**；未支付时为 null 是**正常状态**，别据此判定异常。 */
  supplierOrderNo: z.string().nullable().default(null),
})
export type OrderSummary = z.infer<typeof orderSummarySchema>

/**
 * 订单详情（`OrderDetailView`）。比列表多的就是 `domainPayload`。
 *
 * ⚠⚠ `domainPayload` 是 **JSON 字符串**，不是对象 —— 前端要 `JSON.parse` 一次。
 *   服务端不解析、原样透传（公共层不解释其内容）。解析失败要降级，不能白屏。
 */
export const orderDetailSchema = z.looseObject({
  orderNo: z.string().min(1),
  state: z.string().min(1),
  totalAmount: z.number().nullable().default(null),
  currency: z.string().nullable().default(null),
  supplierOrderNo: z.string().nullable().default(null),
  createdAt: z.string().nullable().default(null),
  /** ⚠ JSON 字符串。可能为 null（域没落 payload）。 */
  domainPayload: z.string().nullable().default(null),
})
export type OrderDetail = z.infer<typeof orderDetailSchema>

/**
 * 取消结果（`CancelResultView`）。
 *
 * ⚠⚠ **`status` 是取消后的真实新状态，不一定是 `ORDER_CANCELLED`**：已付款的订单取消后落
 *   `ORDER_REFUNDING`（有钱要退，不能进终态）。前端**必须按它显示**，别写死「取消成功 = 已取消」，
 *   那会让用户以为钱已经退了。
 */
export const cancelResultSchema = z.looseObject({
  status: z.string().min(1),
})
export type CancelResult = z.infer<typeof cancelResultSchema>

/** 取消请求体。`reason` 可空，但给几个常用原因让用户点，事后客诉才复现得了。 */
export const cancelOrderRequestSchema = z.looseObject({
  reason: z.string().max(200).optional(),
})
export type CancelOrderRequest = z.infer<typeof cancelOrderRequestSchema>
